import React from 'react';
import { PropTypes } from 'prop-types';
import styled from 'styled-components';
import { marked } from 'marked';
import AutoGrowTextArea from '../AutoGrowTextArea';
import Constants from '../../submodules/logictry_config/constants';
import OpenAI from '../../services/OpenAI';
import SidebarService from '../../services/SidebarService';
import UserCache from '../../services/cache/UserCache';
import WindowSize from '../../services/WindowSize';
import UserAccount from '../../services/UserAccount';
import { ArrowUpIcon, ArrowDownIcon, ArrowLeftIcon } from '../../styledhtml/Icon';
import LoadingIndicator from '../LoadingIndicator';
import UserAvatar from '../UserAvatar';
import ScrollIt from '../../utils/Scroll';
import ClickableDiv from '../ClickableDiv';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';

const Wrapper = styled.div`
  position: absolute;
  inset: 0;
  background: white;
  > div {
    overflow: auto;
  }
  > div:first-child {
    background: white;
    border-radius: 0.5rem 0.5rem 0 0;
    padding: 1rem;
    flex: 1;
    position: absolute;
    inset: 1rem 1rem 7rem;
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    > div {
      width: 100%;
      max-width: 48rem;
      flex-shrink: 0;
    }
  }
  > div:last-child {
    border-radius: 0 0 0.5rem 0.5rem;
    background: white;
    padding: 0 0 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    bottom: 0rem;
    width: calc(100% - 2rem);
    margin: 1rem;
    align-items: center;
    > div:first-child {
      display: flex;
      align-items: center;
      width: 48rem;
      max-width: calc(100% - 2rem);
      background: white;
      box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.1) 0px 4px 6px -4px;
      border: ${Constants.LightBorder};
      border-radius: 1.5rem;
      padding: 1rem 1rem 1rem 1.5rem;
      > div:last-child {
        margin-left: 0.5rem;
      }
    }
    button {
      background: black;
      color: white;
      padding: 0.5rem 1rem;
      border-radius: 0.5rem;
    }
  }
`;

export default class ChatInterface extends LogictryComponent {
  static propTypes = {
    report: PropTypes.bool,
    tree: PropTypes.object,
    collection: PropTypes.object,
    style: PropTypes.object,
    hide: PropTypes.bool,
  }
  state = {
    question: '',
    messages: [{}],
    busy: false,
    isScrolling: false,
    chatRefs: {},
  }
  componentDidMount() {
    OpenAI.onStateUpdate(this.handleOpenAIResponse);
    UserAccount.onStateUpdate(this);
    UserCache.onStateUpdate(this);
    WindowSize.onStateUpdate(this);
    const { tree, report, collection } = this.props;
    if (tree) {
      if (report) {
        this.state.busy = true;
        this.forceUpdate();
        OpenAI.askQuestion(null, tree, true);
      } else {
        this.setState({
          messages: [{
            assistant: `Hi, I am a chatbot capable of answering any questions relating to "${tree.title.text}".  What would you like to ask?`
          }]
        });
      }
    } else if (collection) {
      this.setState({
        messages: [{
          assistant: `Hi, I am a chatbot capable of answering any questions relating to "${collection.title}".  What would you like to ask?`
        }]
      });
    }
  }
  componentWillUnmount() {
    OpenAI.offStateUpdate(this.handleOpenAIResponse);
    UserAccount.offStateUpdate(this);
    UserCache.offStateUpdate(this);
    WindowSize.offStateUpdate(this);
    OpenAI.cancelRequests();
    clearTimeout(this.updateAssistantMessagesTimeout);
    clearTimeout(this.scrollBottomTimeout);
    this.state.chatRefs = null;
  }
  handleOpenAIResponse = () => {
    const { messages } = this.state;
    const { response, complete, error } = OpenAI.openAIState;
    if (error) {
      this.state.tooLong = ['One of the sections is too long'];
      this.forceUpdate();
      return;
    }
    messages[messages.length - 1].assistant = response;
    if (complete) {
      this.state.busy = false;
      this.forceUpdate();
    }
    this.checkScroll();
    this.forceUpdate();
  }
  askQuestion = async () => {
    const { mobile } = WindowSize;
    if (mobile) {
      document.activeElement?.blur();
    }
    const { question, busy } = this.state;
    if (!question) return;
    if (busy) return;
    this.state.busy = true;
    this.state.question = '';
    this.forceUpdate();

    const { messages } = this.state;
    let answer = '';
    messages.push({ user: question });
    messages.push({ assistant: answer });
    this.state.messages = messages;
    this.forceUpdate();
    clearTimeout(this.scrollBottomTimeout);
    this.scrollBottomTimeout = setTimeout(() => {
      this.scrollBottom();
    }, 100);

    const { tree, report, collection } = this.props;
    OpenAI.askQuestion(question, tree, report, collection, messages);
  }
  scrollBottom = () => {
    const chatbotMessages = document.getElementById('chatinterface__messages');
    this.setState({ isScrolling: true });
    ScrollIt(chatbotMessages.scrollHeight, chatbotMessages, 700, 'easeInOutQuad', () => {
      this.setState({ isScrolling: false });
    });
  }
  checkScroll = () => {
    const chatbotMessages = document.getElementById('chatinterface__messages');
    if (!chatbotMessages) return;
    const isAtBottom = Math.abs(chatbotMessages.scrollHeight - chatbotMessages.clientHeight - chatbotMessages.scrollTop) < 1;
    this.setState({ showScrollDownButton: !isAtBottom });
  }
  updateAssistantMessages = () => {
    const { messages } = this.state;
    messages.forEach((message, i) => {
      if (!message.assistant) return;
      if (!this.state.chatRefs || !this.state.chatRefs[i]) return;
      const renderer = new marked.Renderer();
      renderer.link = (href, title, text) => {
        if (typeof href === 'object') {
          text = href.text;
          title = href.title;
          href = href.href;
        }
        return `<a href="${href}"${title ? ` title="${title}"` : ''} target="_blank" rel="noopener noreferrer">${text}</a>`;
      };
      this.state.chatRefs[i].innerHTML = marked.parse(message.assistant, { renderer });
    });
  }
  render() {
    const { style, tree, collection, report, hide } = this.props;
    const { mobile } = WindowSize;
    const { isApp } = Constants;
    const { messages, busy, question, tooLong, showScrollDownButton, isScrolling } = this.state;
    if (tooLong && tooLong.length > 0) return <div
      style={{
        overflow: 'auto',
        padding: '2rem',
        ...style,
        position: 'absolute',
        top: 0, left: 0, bottom: 0,
        background: 'white',
        visibility: hide ? 'hidden' : null
      }}
    >
      <h1>Chatbot Disabled</h1>
      <p>The following sections are too long and must be broken into smaller sections</p>
      {tooLong.map((sentence, i) => (
        <div key={i} style={{ borderTop: '1px solid black', borderBottom: '1px solid black' }}>
          <h2>Section {i + 1} - {sentence.split(/\s+/).length} words</h2>
          <div dangerouslySetInnerHTML={{ __html: sentence }} />
        </div>
      ))}
    </div>;
    const user = (UserCache.getUsersByIds([tree?.owner || collection?.owner]) || [])[0];
    clearTimeout(this.updateAssistantMessagesTimeout);
    this.updateAssistantMessagesTimeout = setTimeout(() => {
      this.updateAssistantMessages();
    });
    return (
      <Wrapper style={{
        background: tree && (tree.isCustomApp && tree.background && tree.background.color || tree.root.backgroundColor),
        ...style,
        visibility: hide ? 'hidden' : null
      }}>
        <div
          id="chatinterface__messages"
          onScroll={this.checkScroll}
        >
          {report && (<ClickableDiv onClick={() => { if (!busy) SidebarService.updateSelectType('Tree') }} style={{
            display: 'flex',
            alignItems: 'center',
            visibility: busy && 'hidden' || null
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '2rem',
              width: '2rem',
              borderRadius: '1rem',
              padding: busy && '0.15rem' || '0.5rem',
              marginRight: 13
            }}>
              <ArrowLeftIcon style={{ width: 14, height: 14 }} />
            </div>
            <div>Start Over</div>
          </ClickableDiv>)}
          {messages.map((message, i) => (
            <div
              key={i}
              style={{
                margin: '0.5rem 0',
                display: 'flex',
                gap: '0.5rem',
                justifyContent: message.user && 'flex-end' || null,
                minHeight: (messages.length - 1 === i) && 'calc(100% - 9rem)' || null
              }}
            >
              {message.assistant && <div style={{ margin: '8px 4px 0 0' }}>
                <UserAvatar user={user} />
              </div>}
              <div
                style={{
                  backgroundColor: message.user && 'rgba(233, 233, 233, 0.5)' || null,
                  padding: message.user && '0.75rem 1.25rem' || null,
                  borderRadius: '1.5rem',
                  maxWidth: message.user && '70%' || null,
                }}
              >
                {message.user && <div style={{
                  display: 'inline',
                  whiteSpace: 'pre-wrap',
                }}>{message.user}</div>}
                {message.assistant && <div
                  ref={(e) => { if (this.state.chatRefs) { this.state.chatRefs[i] = e; } }}
                />}
              </div>
            </div>
          ))}
          <div style={{ height: '4rem', flexShrink: 0 }}></div>
        </div>
        {(showScrollDownButton && !isScrolling) && <div
          onClick={this.scrollBottom}
          style={{ position: 'absolute', bottom: '8rem', left: 'calc(50% - 2rem)', height: '2rem', width: '2rem', background: 'white', border: Constants.LightBorder, borderRadius: '50%', padding: '0.4rem', cursor: 'pointer' }}
        >
          <ArrowDownIcon />
        </div>}
        <div>
          <div onKeyDown={(e) => { if (e.key === 'Enter') this.askQuestion(); }}>
            <AutoGrowTextArea
              disabled={false}
              tabIndex={-1}
              onChange={(question) => {
                this.state.question = question;
                this.forceUpdate();
              }}
              innerRef={(e) => {
                if (isApp && !mobile && e && (!e.initialFocus || hide !== e.lastHideSetting)) {
                  e.focus();
                  e.initialFocus = true;
                  e.lastHideSetting = hide;
                }
              }}
              placeholder={'Ask anything'}
              defaultValue={question}
              className={''}
              maxHeight={220}
            />
            <div>
              <div
                onClick={this.askQuestion}
                style={{
                  height: '2rem',
                  width: '2rem',
                  borderRadius: '1rem',
                  color: 'white',
                  background: !busy && 'rgba(233, 233, 233, 0.5)' || null,
                  padding: busy && '0.15rem' || '0.5rem'
                }}
              >
                {busy && <LoadingIndicator /> || <ArrowUpIcon />}
              </div>
            </div>
          </div>
        </div>
      </Wrapper>
    );
  }
}
